import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import config from '../config';
import { useAuth } from '../context/AuthContext';

const SortableItem = ({ id, category, onEdit, onDelete, depth = 0 }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        marginLeft: `${depth * 24}px`
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="d-flex align-items-center justify-content-between p-12 bg-base rounded border mb-8 hover-bg-base-50 transition-1">
            <div className="d-flex align-items-center gap-12">
                <Icon icon="mdi:drag" className="text-secondary-light cursor-grab" />
                {category.icon && <Icon icon={category.icon} className="text-xl" style={{ color: category.color || 'inherit' }} />}
                <div className="d-flex flex-column">
                    <span className="fw-medium text-primary-light">{category.name}</span>
                    {category.name_ar && <span className="text-xs text-secondary-light">{category.name_ar}</span>}
                </div>
            </div>
            <div className="d-flex gap-8">
                <button className="btn btn-sm btn-icon btn-outline-primary" onClick={(e) => { e.stopPropagation(); onEdit(category); }} aria-label="Edit category">
                    <Icon icon="mdi:pencil" />
                </button>
                <button className="btn btn-sm btn-icon btn-outline-danger" onClick={(e) => { e.stopPropagation(); onDelete(category.id); }} aria-label="Delete category">
                    <Icon icon="mdi:delete" />
                </button>
            </div>
        </div>
    );
};

const VenueMenuCategories = ({ menuId, onCategoriesChange }) => {
    const { auth } = useAuth();
    const [categories, setCategories] = useState([]);
    const [isAdding, setIsAdding] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formData, setFormData] = useState({ name: '', name_ar: '', icon: '', color: '#000000', parent_id: '' });
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const fetchCategories = useCallback(async () => {
        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/menus/${menuId}/categories`);
            if (res.ok) {
                const data = await res.json();
                setCategories(data);
                if (onCategoriesChange) onCategoriesChange(data);
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to load categories');
        }
    }, [auth, menuId, onCategoriesChange]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            setCategories((items) => {
                const oldIndex = items.findIndex((i) => i.id === active.id);
                const newIndex = items.findIndex((i) => i.id === over.id);
                const newItems = arrayMove(items, oldIndex, newIndex);
                
                // Persist order
                const reorderData = newItems.map((item, index) => ({
                    id: item.id,
                    display_order: index,
                    parent_id: item.parent_id
                }));
                
                // Optimistic update done, now save to server
                auth.fetch(`${config.API_BASE_URL}/api/menus/categories/reorder`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ items: reorderData })
                }).catch(err => {
                    console.error(err);
                    toast.error('Failed to save order');
                });

                return newItems;
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const url = editingCategory 
                ? `${config.API_BASE_URL}/api/menus/categories/${editingCategory.id}`
                : `${config.API_BASE_URL}/api/menus/${menuId}/categories`;
            
            const method = editingCategory ? 'PUT' : 'POST';
            
            const res = await auth.fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...formData,
                    parent_id: formData.parent_id || null,
                    display_order: editingCategory ? editingCategory.display_order : categories.length
                })
            });

            if (res.ok) {
                toast.success(`Category ${editingCategory ? 'updated' : 'created'}`);
                setIsAdding(false);
                setEditingCategory(null);
                setFormData({ name: '', name_ar: '', icon: '', color: '#000000', parent_id: '' });
                fetchCategories();
            } else {
                const err = await res.json();
                toast.error(err.message || 'Operation failed');
            }
        } catch (error) {
            console.error(error);
            toast.error('Error saving category');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure? This will remove this category.')) return;
        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/menus/categories/${id}`, { method: 'DELETE' });
            if (res.ok) {
                toast.success('Category deleted');
                fetchCategories();
            } else {
                toast.error('Failed to delete');
            }
        } catch (error) {
            console.error(error);
        }
    };

    const startEdit = (cat) => {
        setEditingCategory(cat);
        setFormData({ name: cat.name, name_ar: cat.name_ar || '', icon: cat.icon || '', color: cat.color || '#000000', parent_id: cat.parent_id || '' });
        setIsAdding(true);
    };

    // Helper to organize categories for dropdown
    const mainCategories = categories.filter(c => !c.parent_id);
    
    // Filter categories for search
    const filteredCategories = categories.filter(c => 
        c.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="category-manager">
            <div className="d-flex justify-content-between align-items-center mb-16 gap-16">
                <h6 className="mb-0 text-nowrap">Categories</h6>
                <div className="input-group input-group-sm flex-grow-1" style={{maxWidth: '300px'}}>
                    <span className="input-group-text bg-white border-end-0"><Icon icon="mdi:magnify" className="text-secondary-light" /></span>
                    <input 
                        type="text" 
                        className="form-control border-start-0 ps-0" 
                        placeholder="Search categories..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <button className="btn btn-primary-600 btn-sm text-nowrap" onClick={() => { setIsAdding(!isAdding); setEditingCategory(null); setFormData({ name: '', name_ar: '', icon: '', color: '#000000', parent_id: '' }); }}>
                    <Icon icon={isAdding ? 'mdi:close' : 'mdi:plus'} className="me-1" />
                    {isAdding ? 'Cancel' : 'Add'}
                </button>
            </div>

            {isAdding && (
                <div className="card bg-base-50 p-16 mb-16 border animate__animated animate__fadeIn">
                    <form onSubmit={handleSubmit}>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label">Name (English)</label>
                                <input 
                                    type="text" 
                                    className="form-control" 
                                    value={formData.name} 
                                    onChange={e => setFormData({...formData, name: e.target.value})} 
                                    required 
                                    placeholder="e.g. Drinks"
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Name (Arabic)</label>
                                <input 
                                    type="text" 
                                    className="form-control" 
                                    value={formData.name_ar} 
                                    onChange={e => setFormData({...formData, name_ar: e.target.value})} 
                                    placeholder="e.g. مشروبات"
                                    dir="rtl"
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Parent Category (Optional)</label>
                                <select 
                                    className="form-select" 
                                    value={formData.parent_id} 
                                    onChange={e => setFormData({...formData, parent_id: e.target.value})}
                                >
                                    <option value="">None (Main Category)</option>
                                    {mainCategories
                                        .filter(c => c.id !== editingCategory?.id) // Prevent self-parenting
                                        .map(c => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Icon (Iconify string)</label>
                                <div className="input-group">
                                    <span className="input-group-text"><Icon icon={formData.icon || 'mdi:circle-outline'} /></span>
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        value={formData.icon} 
                                        onChange={e => setFormData({...formData, icon: e.target.value})} 
                                        placeholder="mdi:coffee"
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Color Code</label>
                                <input 
                                    type="color" 
                                    className="form-control form-control-color w-100" 
                                    value={formData.color} 
                                    onChange={e => setFormData({...formData, color: e.target.value})} 
                                />
                            </div>
                            <div className="col-12 text-end">
                                <button type="submit" className="btn btn-primary-600" disabled={loading}>
                                    {loading ? 'Saving...' : (editingCategory ? 'Update' : 'Create')}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            )}

            {searchTerm ? (
                <div className="category-list">
                    {filteredCategories.map(cat => (
                         <div key={cat.id} className="d-flex align-items-center justify-content-between p-12 bg-base rounded border mb-8">
                            <div className="d-flex align-items-center gap-12">
                                {cat.icon && <Icon icon={cat.icon} className="text-xl" style={{ color: cat.color || 'inherit' }} />}
                                <div className="d-flex flex-column">
                                    <span className="fw-medium text-primary-light">{cat.name}</span>
                                    {cat.name_ar && <span className="text-xs text-secondary-light">{cat.name_ar}</span>}
                                </div>
                                {cat.parent_id && <span className="badge bg-neutral-200 text-secondary-light">Subcategory</span>}
                            </div>
                            <div className="d-flex gap-8">
                                <button className="btn btn-sm btn-icon btn-outline-primary" onClick={() => startEdit(cat)} aria-label="Edit category">
                                    <Icon icon="mdi:pencil" />
                                </button>
                                <button className="btn btn-sm btn-icon btn-outline-danger" onClick={() => handleDelete(cat.id)} aria-label="Delete category">
                                    <Icon icon="mdi:delete" />
                                </button>
                            </div>
                        </div>
                    ))}
                    {filteredCategories.length === 0 && <p className="text-center text-secondary-light py-12">No matching categories found.</p>}
                </div>
            ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={categories} strategy={verticalListSortingStrategy}>
                        <div className="category-list">
                            {mainCategories.map(main => (
                                <React.Fragment key={main.id}>
                                    <SortableItem 
                                        id={main.id} 
                                        category={main} 
                                        onEdit={startEdit} 
                                        onDelete={handleDelete} 
                                    />
                                    {categories.filter(sub => sub.parent_id === main.id).map(sub => (
                                        <SortableItem 
                                            key={sub.id} 
                                            id={sub.id} 
                                            category={sub} 
                                            onEdit={startEdit} 
                                            onDelete={handleDelete} 
                                            depth={1} 
                                        />
                                    ))}
                                </React.Fragment>
                            ))}
                            {categories.length === 0 && <p className="text-secondary-light text-center py-24">No categories yet. Add one to get started!</p>}
                        </div>
                    </SortableContext>
                </DndContext>
            )}
        </div>
    );
};

export default VenueMenuCategories;
